// AI Insurance Coaching - TypeScript Types
// Matches the localStorage data model from the spec

export type UserRole = 'ADMIN' | 'USER';
export type UserStatus = 'active' | 'inactive' | 'pending';
export type UserLevel = 'rookie' | 'basic';

// Back-compat alias used by some pages/components.
export type LearnerLevel = UserLevel;

export interface User {
  /** Required — set from token at login */
  id: string;
  uuid: string;
  role: UserRole;
  name: string;
  email: string;
  fusionauthUserId?: string;

  /** Optional — defaults applied by addUser */
  fusionauthTenantId?: string;
  username?: string | null;
  language?: string | null;
  timezone?: string | null;
  sessions?: number;
  avgScore?: number;
  trend?: string;
  levelCode?: string;
  status?: UserStatus;
  level?: UserLevel;
  avatar?: string | null;
  gender?: string;
  /** Calendar date `YYYY-MM-DD`; null for accounts created before the field existed. */
  birthDate?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface Persona {
  id: string;
  name: string;
  avatar?: string;
  /** Profiling code stored in `persona.profiling.mainBasicGender`. Drives voice/avatar pick. */
  mainBasicGenderCode?: string | null;
  /** Resolved label for the gender code. */
  genderName?: string | null;
  /** Resolved label for the age range code. */
  ageRangeName?: string | null;
  insuranceTypeNames?: string[];
  wvs?: string[];
  /** Raw `additional_information` JSONB from persona table. Carries `systemPrompt`. */
  additionalInformation?: Record<string, unknown> | null;
  /**
   * Raw `profiling` JSONB, carried through untouched. The roleplay persona panel
   * renders every configured field from it, and reads the `<fieldCode>_specific`
   * keys beside each band code — without it the panel can only show the two
   * fields that arrive pre-resolved (`genderName`, `ageRangeName`).
   */
  profiling?: Record<string, unknown> | null;
}

/**
 * The difficulty codes Core accepts. Single source of truth: the type is
 * derived from the tuple rather than written out a second time, and callers
 * that forward a value from elsewhere (the AI's `difficulty_level`) can check
 * membership instead of casting.
 */
export const SCENARIO_DIFFICULTIES = ['easy', 'medium', 'hard'] as const;
export type ScenarioDifficulty = (typeof SCENARIO_DIFFICULTIES)[number];
export type ScenarioChannel = 'phone' | 'video' | 'chat' | 'in-person';
export type ScenarioStatus = 'active' | 'draft' | 'archived';

/**
 * One criterion a scenario is authored against. Replaced the former
 * `ScenarioSkill`: scenarios target observable signals directly rather than
 * whole skills.
 */
export interface ScenarioObservableSignal {
  /** UUID of the scenario↔signal link, not of the signal itself. */
  uuid: string;
  /** The signal itself — what Core takes back when resolving extra_configs. */
  observableSignalUuid: string;
  observableSignalCode: string;
  observableSignalNameKey?: string | null;
  /** Authored display name when present (preferred over nameKey). */
  observableSignalName?: string | null;
  observableSignalDescription: string | null;
  /** Rubric ids this signal is graded by (`op4`, `dc1`, …). */
  metaDataKeys: string[];
  /** Stage the signal is graded under (`opening`, `discovery`, …). */
  stageCode: string;
  stageNameKey?: string | null;
  /** Authored stage display name when present. */
  stageName?: string | null;
}

/** A stage as a scenario summary shows it: its i18n key, with the code as fallback. */
export interface ScenarioStage {
  code: string;
  /** Translation key straight from Core (`stages.opening`) — never assembled here. */
  nameKey?: string | null;
  /** Authored display name when present (preferred over nameKey). */
  name?: string | null;
}

/**
 * The stages a scenario touches, in stage order, each listed once.
 *
 * Scenarios are summarised by stage rather than by signal: a scenario routinely
 * carries a dozen signals, which reads as a wall of jargon, while the two or
 * three stages it spans is the shape a learner recognises. Core already returns
 * the signals in stage order, so first-seen order is stage order.
 */
export function scenarioStages(
  signals: ScenarioObservableSignal[] | undefined | null
): ScenarioStage[] {
  const seen = new Map<string, ScenarioStage>();
  for (const signal of signals ?? []) {
    if (signal.stageCode && !seen.has(signal.stageCode)) {
      seen.set(signal.stageCode, {
        code: signal.stageCode,
        nameKey: signal.stageNameKey,
        name: signal.stageName,
      });
    }
  }
  return [...seen.values()];
}

export interface ScenarioStep {
  id?: string;
  order: number;
  title?: string;
  description: string;
  expectedAction: string;
}

export interface Scenario {
  uuid: string;
  /** NULL = common (org-wide, admin-published). Set = unique, owned by that agent/learner. */
  ownerUserPublicId?: string | null;
  /** Owner's display name; null for a common scenario or a deleted owner. */
  ownerName?: string | null;
  /** Absent until the server sends a content scope. */
  visibility?: 'system' | 'organization' | 'personal';
  /** Organization the content belongs to; null for system content. */
  organization?: { uuid: string; name: string } | null;
  language: string | null;
  title: string;
  /** Author's brief from create; null for scenarios created before this field existed. */
  description?: string | null;
  durationMin: number;
  /**
   * @deprecated Core no longer returns this — it was the common item's bigint
   * primary key, which the API does not expose. Use `status`.
   */
  statusItemId?: string;
  status: ScenarioStatus;
  createdAt: string;
  updatedAt: string;
  observableSignals: ScenarioObservableSignal[];
  tasks?: unknown[];
  /** Opening context the AI resumes from; null runs the full stage flow. */
  beginContext?: string | null;
  /** Business domain (e.g. `banca`); null falls back to `insurance` on init. */
  domain?: string | null;
  /**
   * Employer the agent works for in this roleplay, free text as authored
   * ('Generali', 'Generali Viet Nam'). Forwarded as `agent_info.company`,
   * which the AI normalises itself; null on scenarios authored before the
   * field existed.
   */
  company?: string | null;
  /** Authored difficulty code, stored on the scenario. */
  difficulty: ScenarioDifficulty;
  /** Server-computed per `AccessPolicy.forContent`; false on an unscoped read. */
  canEdit?: boolean;
  canDelete?: boolean;
  // Legacy/UI fields - optional for API responses
  id?: string;
  targetSkills?: string[];
  /** Factors the scenario was authored with; an unpicked one is stored as the `default` row. */
  metaData?: {
    relationshipUuid?: string | null;
    skepticismUuids?: string[];
  } | null;
  appliedSkills?: Array<{
    skillUuid: string;
    skillName: string;
    difficulty: ScenarioDifficulty;
  }>;
}

export type RoleplayState =
  | 'greeting'
  | 'discovery'
  | 'proposal'
  | 'objection'
  | 'close'
  | 'completed';

export interface AiTask {
  name: string;
  description?: string | null;
  evidence_quotes?: string[] | null;
  reasoning?: string | null;
  is_completed?: boolean | null;
}

export interface SessionMessage {
  id: string;
  sender: 'learner' | 'ai';
  content: string;
  timestamp: string;
  state: RoleplayState;
  /**
   * Active roleplay elapsed seconds when this message was sent — the real
   * "minute N of the roleplay" (pause-aware, from the resumable session timer),
   * not wall-clock. Persisted verbatim in the Core `messages` jsonb. Optional:
   * absent on sessions recorded before this field existed.
   */
  roleplayElapsedSec?: number;
}

export interface SessionScores {
  content: number;
  total: number;
}

export interface VoiceTask {
  name: string;
  description?: string;
  is_completed?: boolean;
  evidence_quotes?: string[];
  reasoning?: string;
}

export type RoleplayMode = 'voice' | 'avatar' | 'simulator';

/** Every mode a session can run in, in the order the start screens offer them. */
export const ROLEPLAY_MODES: readonly RoleplayMode[] = ['voice', 'avatar', 'simulator'];

/** Mode a session starts in when nothing names one. Must match Core's own default. */
export const DEFAULT_ROLEPLAY_MODE: RoleplayMode = 'voice';

/**
 * Live coaching suggestion for the current turn — one object with three distinct
 * roles (not interchangeable lines). Snake_case matches the AI payload as-is.
 */
export interface LiveSuggestion {
  /** 🎯 strategic direction — what to do next, NOT a line to say aloud. */
  next_best_action: string;
  /** 🏷️ phrases worth weaving in (rendered as static chips). */
  positive_keywords: string[];
  /** ✨ one ready-to-say sample line. */
  sample_message: string;
}

/** One turn's AI suggestion, accumulated per turn (history, not cleared). */
export interface SuggestionHistoryEntry {
  /** id of the last message present when this suggestion was generated (the AI line it follows). */
  afterMessageId: string | null;
  /** epoch ms when generated. */
  generatedAt: number;
  /** the coaching suggestion offered to the agent this turn. */
  suggestion: LiveSuggestion;
}

export interface Session {
  id: string;
  scenarioId: string;
  personaUuid?: string;
  aiSessionId?: string;
  startedAt: string;
  endedAt?: string;
  messages: SessionMessage[];
  tasksInitial?: AiTask[];
  tasksLive?: VoiceTask[];
  tasksFinal?: AiTask[];
  liveSuggestions?: LiveSuggestion | null;
  /** Accumulated per-turn suggestion history (persisted; powers review export). */
  suggestionHistory?: SuggestionHistoryEntry[];
  conclusionSummary?: string | null;
  lockTabId?: string | null;
  lockAt?: string | null;
  scores?: SessionScores;
  stateTrail: RoleplayState[];
  aiTasks?: AiTask[];
  mode?: RoleplayMode | null;
  /** Accumulated active roleplay seconds (server-persisted; cross-device resume). */
  roleplayElapsedSec?: number;
  /** Regional dialect/accent for the AI customer (VI sessions) — persisted so resume on any device stays consistent. */
  regionalStyle?: string | null;
  /** TTS provider chosen at session start (e.g. 'vieneu' | 'vnpt') — persisted so resume re-inits the same voice. */
  ttsProvider?: string | null;
  /**
   * Factors the learner picked at session start, as factor-library uuids —
   * persisted for the same reason `regionalStyle` is: the picker and the screen
   * that opens the AI socket are different routes, and a resume has to reach
   * the same configuration.
   */
  metaData?: {
    relationshipUuid?: string | null;
    skepticismUuids?: string[];
  } | null;
}

export interface RubricItem {
  stageId: string;
  score: number;
  maxScore: number;
  feedback: string;
}

export type SentimentTimelineTuple = [score: number, timestamp: string, reason: string];

export type SentimentTimelineRaw = Record<string, SentimentTimelineTuple>;

export interface SentimentTimelinePoint {
  turn: number;
  score: number;
  timestamp: string;
  reason: string;
}

export interface Report {
  sessionId: string;
  rubricBreakdown: RubricItem[];
  strengths: string[];
  gaps: string[];
  recommendations: string[];
  sentimentTimeline?: SentimentTimelinePoint[];
}

// AI Assessment types (from sales-coach-ai)

/** Per-criterion score within a stage. Persisted for record, not displayed. */
export interface LinkedCriterion {
  criteria_name: string;
  score: number; // 0-100
}

export interface TopicAssessment {
  /**
   * @deprecated Old per-topic axis. Superseded by `stage_id`; kept transiently
   * during the additive topic→stage migration so no reader silently mismatches.
   */
  topic_code?: string;
  /** Conversation stage id (opening/discovery/diagnosis/tailoring/objections). */
  stage_id?: string;
  score: number; // 0-100
  /** Per-criterion detail within the stage — persisted first-class, not displayed. */
  linked_criteria?: LinkedCriterion[];
  summary: string;
  strengths: string[];
  improvements: string[];
  suggestions: string[];
}

export interface AiAssessmentResult {
  /** Topic assessments — normalized from AI's `content` field */
  results: TopicAssessment[];
  sentimentTimeline?: SentimentTimelineRaw;
}

export type NotificationSeverity = 'info' | 'warning' | 'error' | 'success';

export interface Notification {
  id: string;
  role: UserRole;
  title: string;
  message: string;
  severity: NotificationSeverity;
  createdAt: string;
  read: boolean;
  meta?: Record<string, unknown>;
}

export interface UIPrefs {
  sidebarCollapsed: boolean;
  theme: 'light' | 'dark';
}

// Auth state. The user's public id is NOT stored here — it's derived live from the token via
// getUserPublicId() (single source of truth), so this only carries session-level auth facts.
export interface AuthState {
  isAuthenticated: boolean;
  role: UserRole | null;
}

// Script bank for deterministic AI responses
export interface ScriptResponse {
  state: RoleplayState;
  keywords: string[];
  response: string;
}

// Analytics data types
export interface UserProgress {
  userId: string;
  sessionsCompleted: number;
  averageScore: number;
  scoreHistory: { date: string; score: number }[];
  skillsImproved: string[];
  areasToImprove: string[];
}

export interface TeamAnalytics {
  totalUsers: number;
  activeUsers: number;
  averageTeamScore: number;
  sessionsTodayCount: number;
  coachingCasesOpen: number;
  topPerformers: { userId: string; name: string; score: number }[];
  bottomPerformers: { userId: string; name: string; score: number }[];
  scoreDistribution: { range: string; count: number }[];
}
